import { Alert, Box, Button, FormGroup, OutlinedInput, Stack, Typography } from '@mui/material'
import React from 'react'
import { i18n } from '../../index/index'
import { useValueFormat } from './valueFormat'

export interface YearlyTargetFieldProps {
  value: number
  previousValue: number
  /** Editor is in edit mode. */
  isEditing: boolean
  /** The user may type a new yearly value (in edit mode). */
  canEditYearlyTarget: boolean
  /** The "divide by budget" action is available (in edit mode). */
  canDivideByBudget: boolean
  targetYear: number
  hasLastYear: boolean
  /** All 12 months have a last-year actual, so the yearly comparison is meaningful. */
  lastYearComplete: boolean
  lastYearTotal: number
  divideByBudgetLabel: string
  /** Error from "divide by budget" (e.g. no budget for the year), shown under the button. */
  divideError?: string | null
  onChange: (value: number) => void
  onDivideByBudget: () => void
}

/**
 * Yearly target header area.
 * - Read-only (view mode, or edit mode without permission to change the yearly value):
 *   big value + inline "vuoden tavoite" label, and a "{lastYear}: X (+Y)" line with the
 *   last-year total actual and the change vs this year's target.
 * - Editable (edit mode + permission): outlined input + "Muutos +X (oli Y)" change indicator.
 * - Divide-by-budget pill is shown in edit mode whenever the year allows it.
 */
export function YearlyTargetField({
  value,
  previousValue,
  isEditing,
  canEditYearlyTarget,
  canDivideByBudget,
  targetYear,
  hasLastYear,
  lastYearComplete,
  lastYearTotal,
  divideByBudgetLabel,
  divideError,
  onChange,
  onDivideByBudget,
}: YearlyTargetFieldProps) {
  const { format, formatSigned } = useValueFormat()
  const showInput = isEditing && canEditYearlyTarget
  // Keep cents so the numbers match the table (rounded to 2 decimals only to drop float noise).
  const roundToCents = (amount: number) => Math.round(amount * 100) / 100
  const change = roundToCents((value || 0) - (previousValue || 0))
  const showChange = showInput && change !== 0
  const lastYear = targetYear - 1
  const lastYearChange = roundToCents((value || 0) - lastYearTotal)

  return (
    <Box marginBottom={2}>
      {!showInput ? (
        <>
          <Stack direction='row' spacing={1} alignItems='baseline' flexWrap='wrap'>
            <Typography variant='h4' fontWeight={700}>
              {format(value || 0)}
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              {i18n.t('admin.monthlyTargets.yearlyTargetLabel')}
            </Typography>
          </Stack>
          {hasLastYear && (
            <Typography variant='caption' color='text.secondary'>
              {lastYearComplete && (value || 0) > 0
                ? i18n.t('admin.monthlyTargets.lastYearTotalLine', {
                    year: lastYear,
                    total: format(lastYearTotal),
                    change: formatSigned(lastYearChange),
                  })
                : i18n.t('admin.monthlyTargets.lastYearTotalOnlyLine', {
                    year: lastYear,
                    total: format(lastYearTotal),
                  })}
            </Typography>
          )}
        </>
      ) : (
        <FormGroup>
          <OutlinedInput
            type='number'
            size='small'
            value={value || ''}
            onChange={(event) => onChange(Number(event.target.value))}
            sx={{ maxWidth: { xs: '100%', sm: 240 } }}
          />
        </FormGroup>
      )}
      {showChange && (
        <Typography variant='caption' display='block' color={change > 0 ? 'success.main' : 'error.main'}>
          {i18n.t('admin.monthlyTargets.yearlyChange', {
            change: formatSigned(change),
            previous: format(previousValue || 0),
          })}
        </Typography>
      )}
      {isEditing && canDivideByBudget && (
        <Button
          variant='outlined'
          color='success'
          sx={{ mt: 1, borderRadius: 5, alignSelf: 'flex-start' }}
          onClick={onDivideByBudget}
        >
          {divideByBudgetLabel}
        </Button>
      )}
      {isEditing && divideError && (
        <Alert severity='error' sx={{ mt: 1 }}>
          {divideError}
        </Alert>
      )}
    </Box>
  )
}
